import { z } from 'zod';

// ===========================================================================
// Core Order Schemas
// ===========================================================================

export const OrderItemSchema = z.object({
  id: z.string().cuid(),
  uploadedFileId: z.string().cuid().optional(),
  quantity: z.number().int().min(1).max(99).optional(),
  paperTypeId: z.string().min(1).optional(),
  printerId: z.string().min(1).optional(),
  orderId: z.string().cuid().nullable().optional(),
  width: z.number().positive().nullable().optional(),
  height: z.number().positive().nullable().optional(),
  widthIn: z.number().positive().nullable().optional(),
  heightIn: z.number().positive().nullable().optional(),
  marginIn: z.number().positive().nullable().optional(),
  galleryWrapIn: z.number().positive().nullable().optional(),
  galleryWrapMirrorEdges: z.boolean().nullable().optional(),
  printQualityWarning: z
    .object({
      type: z.literal('LOW_PPI'),
      ppi: z.number().positive(),
      threshold: z.number().positive(),
      acknowledged: z.boolean().optional(),
      reason: z.string().min(1).optional(),
    })
    .nullable()
    .optional(),
  colorMode: z.enum(['COLOR', 'BLACK_WHITE']).optional(),
  originalWidthPx: z.number().positive().nullable().optional(),
  originalHeightPx: z.number().positive().nullable().optional(),
  originalWidthIn: z.number().positive().nullable().optional(),
  originalHeightIn: z.number().positive().nullable().optional(),
  dpiX: z.number().positive().nullable().optional(),
  dpiY: z.number().positive().nullable().optional(),
  status: z
    .enum([
      'NEW',
      'IN_ORDER',
      'FIRST_ORDER',
      'SECOND_ORDER',
      'PAID',
      'REUPLOAD_IMAGE',
      'REUPLOADED',
      'READY_TO_PRINT',
      'PRINTED',
      'IN_DELIVERY',
      'DELIVERED',
      'RETURNED',
      'CANCELLED',
    ])
    .optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  price: z
    .object({
      value: z.number(),
      currency: z.string().length(3).or(z.string().min(1)),
    })
    .optional(),
  pricingStatus: z.enum(['idle', 'loading', 'succeeded', 'failed']).optional(),
  pricingError: z.string().nullable().optional(),
});

export const ShippingAddressSchema = z.object({
  countryCode: z.string().min(2),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().min(1),
  street1: z.string().min(1),
  street2: z.string().optional(),
  city: z.string().min(1),
  novaposhtaCityRef: z.string().min(1).optional(),
  novaposhtaWarehouseId: z.string().min(1).optional(),
  deliveryMode: z.enum(['BRANCH', 'LOCKER', 'COURIER', 'PICKUP_POINT']).optional(),
  shippingProvider: z.string().min(1).optional(),
  region: z.string().min(1),
  postalCode: z.string().min(1),
  setAsDefault: z.boolean().optional(),
});

export const OrderSchema = z.object({
  id: z.string().cuid().optional().nullable(),
  items: z.array(OrderItemSchema),
  shippingAddress: ShippingAddressSchema.optional(),
  fulfillmentMode: z.enum(['SHIPPING', 'PICKUP']).optional(),
  // We can add other top-level order fields here if needed
});

export const OrderPatchSchema = OrderSchema.partial();
export type OrderPatch = z.infer<typeof OrderPatchSchema>;

export const OrderStepSchema = z.enum([
  'upload',
  'uploadedImages',
  'size',
  'printer',
  'paper',
  'frame',
  'review',
  'address',
  'payment',
  'login',
  'done',
  'checkingForPendingOrders',
]);

export type OrderStep = z.infer<typeof OrderStepSchema>;

// ===========================================================================
// AI Interaction Schemas
// ===========================================================================

export const AIProposedUpdateSchema = z.object({
  intent: z.enum(['update_order', 'clarify_question', 'no_op']),
  proposed_update: OrderSchema.partial().optional(), // AI can propose partial updates
  utterance: z.string(),
  reason: z.string().optional(),
  suggestedActions: z.array(z.string()).optional(),
  mode: z.enum(['AI', 'WAITING_FOR_AGENT', 'HUMAN']).optional(),
  interfaceGuidance: z
    .object({
      gotoStep: OrderStepSchema.optional(),
    })
    .strict(),
});

// Type definitions for State Machine events
export type SupervisorEvent =
  | { type: 'UI.GOTO_STEP'; step: OrderStep }
  | { type: 'UI.NEXT_SUGGESTED' }
  | { type: 'UI.SHOW_LOGIN' }
  | { type: 'UI.IMAGES_UPLOADED' }
  | { type: 'UI.IMAGES_UPDATED' }
  | { type: 'UI.ANNOUNCE_UPLOAD_SUMMARY'; summaryText: string }
  | {
      type: 'UI.REQUEST_AI_PROMPT';
      aiPrompt: string;
      fallbackMessage: string;
      suggestedActions?: string[];
    }
  | { type: 'AUTH.LOGGED_IN' }
  | { type: 'AUTH.LOGGED_OUT' }
  | { type: 'ORDER.CLEAR_FOR_ORDER'; orderId?: string | null; skipSetActiveStep?: boolean }
  | { type: 'ORDER.APPLY_PATCH'; patch: OrderPatch }
  | { type: 'FETCH_PENDING_ORDERS.SUCCESS_WITH_ITEMS'; items: OrderItem[] }
  | { type: 'FETCH_PENDING_ORDERS.SUCCESS_NO_ITEMS' }
  | { type: 'FETCH_PENDING_ORDERS.ERROR'; error: string };

// ===========================================================================
// TypeScript Types inferred from Zod Schemas
// ===========================================================================

export type OrderItem = z.infer<typeof OrderItemSchema>;
export type ShippingAddress = z.infer<typeof ShippingAddressSchema>;
export type Order = z.infer<typeof OrderSchema>;
export type AIProposedUpdate = z.infer<typeof AIProposedUpdateSchema>;
